import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Container } from '@mantine/core';
import Header from '../../components/util/header';
import Background from '../../components/util/background';
import { fetchPermissions, PermissionOption } from '../../../libraries/user';
import { AppState } from '../../client-redux/reducers';
import { Language } from '../../languages/languageHandler';
import { LoginState } from '../../client-redux/login/reducer';
import {
  addDistribuition,
  deleteDistribuition,
  DistribuitionRequests,
  DistribuitionsResponse,
  EventsResponse,
  fetchDistributionInfo,
  fetchEventList,
  updateDistribuition
} from '../../../libraries/distribuition-list';
import { useNavigate, useParams } from 'react-router-dom';
import DistribuitionForm from './distribuition-form';
import CustomModal from '../../components/custom-modal';
import './styles.scss';

export enum MODE {
  //READ = 'read',
  EDIT = 'edit',
  NEW = 'new'
}

const Distribuition = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const login = useSelector<AppState, LoginState>(state => state.login);
  const controller = new AbortController();
  const { mode: modeParam, id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [, /* noResults */ setNoResults] = useState<boolean>(true);
  const [distribuition, setDistribuition] = useState<DistribuitionsResponse>();
  const [events, setEvents] = useState<EventsResponse>();

  const [selectedPermissions, setSelectedPermissions] = useState<PermissionOption[]>([]);

  const initialTab = 'permissions';
  const [activeTab, setActiveTab] = useState<string>(initialTab);

  const [showSuccessDialog, setShowSuccessDialog] = useState<boolean>(false);
  const [showErrorDialog, setShowErrorDialog] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const mode = useMemo(() => {
    switch (modeParam) {
      case MODE.NEW:
      case MODE.EDIT:
        return modeParam as MODE;
      default:
        return MODE.EDIT;
    }
  }, [modeParam]);

  useEffect(() => {
    if (id) {
      getEventsList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, id]);

  useEffect(() => {
    if (mode === MODE.NEW) {
      fetchPermissions().then(permissionsResponse => {
        const preparedPermissions = permissionsResponse.list.map((permission: PermissionOption) => ({
          ...permission,
          selected: false
        }));
        setSelectedPermissions(preparedPermissions);
      });
    } else {
      getDistribuitionInfo();
    }
    getEventsList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getEventsList = () => {
    if (!login.token) return;

    if (loading) {
      controller.abort();
    }

    setLoading(true);

    fetchEventList(controller)
      .then(response => {
        if (response.statusOper.code === 0) {
          setEvents(response);
          setNoResults(false);
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') {
        }
      })
      .finally(() => setLoading(false));
  };

  const getDistribuitionInfo = () => {
    if (!login.token) return;

    if (loading) {
      controller.abort();
    }

    setLoading(true);

    fetchDistributionInfo(Number(id), controller)
      .then(async response => {
        if (response.statusOper.code === 0) {
          setDistribuition(response);
          const permissionsResponse = await fetchPermissions();
          const existingPermissions = response.list[0]?.permissions ?? [];
          const preparedPermissions = permissionsResponse.list.map((permission: PermissionOption) => {
            const isSelected = existingPermissions.some(
              (p: { appId: number; permissionCode: number }) =>
                p.appId === permission.appId && (p.permissionCode & permission.mask) !== 0
            );

            return {
              ...permission,
              selected: isSelected
            };
          });

          setSelectedPermissions(preparedPermissions);
          setNoResults(false);
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') {
        }
      })
      .finally(() => setLoading(false));
  };

  const handleSubmit = async (formData: any) => {
    const controller = new AbortController();

    try {
      const permissionsMap: { [appId: number]: number } = {};
      selectedPermissions.forEach(p => {
        if (p.selected) {
          if (!permissionsMap[p.appId]) {
            permissionsMap[p.appId] = 0;
          }
          permissionsMap[p.appId] |= p.mask;
        }
      });

      const reducedPermissions = Object.entries(permissionsMap).map(([appId, permissionCode]) => ({
        appId: Number(appId),
        permissionCode
      }));

      const requestPayload: DistribuitionRequests = {
        ...formData,
        permissions: reducedPermissions
      };

      let response;

      if (mode === MODE.NEW) {
        response = await addDistribuition(requestPayload, controller);
      } else if (mode === MODE.EDIT) {
        response = await updateDistribuition(requestPayload, controller);
      }

      if (!response || response.statusOper?.code !== 0) {
        const code = response?.statusOper?.code ?? 'UNKNOWN';
        const message = response?.statusOper?.message ?? 'Unexpected error';

        const baseError =
          mode === MODE.NEW
            ? lang.ADD_DISTRIBUITION_ERROR || 'Failed to add distribution'
            : lang.EDIT_DISTRIBUITION_ERROR || 'Failed to update distribution';

        setErrorMessage(`${baseError} - ${code}, ${message}`);
        setShowErrorDialog(true);

        setTimeout(() => {
          setShowErrorDialog(false);
        }, 2000);
      } else {
        setSuccessMessage(
          mode === MODE.NEW
            ? lang.ADD_DISTRIBUITION_SUCCESS || 'Distribution successfully added'
            : lang.EDIT_DISTRIBUITION_SUCCESS || 'Distribution successfully updated'
        );
        setShowSuccessDialog(true);
        setTimeout(() => {
          setShowErrorDialog(false);
          navigate('/distributions');
        }, 2000);
      }
    } catch (err: any) {
      const baseError =
        mode === MODE.NEW
          ? lang.ADD_DISTRIBUITION_ERROR || 'Failed to add distribution'
          : lang.EDIT_DISTRIBUITION_ERROR || 'Failed to update distribution';

      const code = err?.statusOper?.code || err?.code || 'EXCEPTION';
      const message = err?.statusOper?.message || err?.message || 'An unexpected error occurred';

      setErrorMessage(`${baseError} - ${code}, ${message}`);
      setShowErrorDialog(true);

      setTimeout(() => setShowErrorDialog(false), 2000);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await deleteDistribuition(distribuition?.list[0].id || '0');

      if (!response || response.statusOper?.code !== 0) {
        const code = response?.statusOper?.code ?? 'UNKNOWN';
        const message = response?.statusOper?.message ?? 'Unexpected error';
        const baseError = lang.DELETE_DISTRIBUITION_ERROR || 'Failed to delete distribution';

        setErrorMessage(`${baseError} - ${code}, ${message}`);
        setShowErrorDialog(true);

        setTimeout(() => setShowErrorDialog(false), 2000);
      } else {
        setSuccessMessage(lang.DELETE_DISTRIBUITION_SUCCESS || 'Distribution successfully deleted');
        setShowSuccessDialog(true);

        setTimeout(() => {
          setShowSuccessDialog(false);
          navigate('/distributions');
        }, 2000);
      }
    } catch (err: any) {
      setErrorMessage(
        `${lang.DELETE_DISTRIBUITION_ERROR || 'Failed to delete distribution'} - ${err?.statusOper?.code || err?.code || 'EXCEPTION'}, ${err?.statusOper?.message || err?.message || 'An unexpected error occurred'}`
      );
      setShowErrorDialog(true);

      setTimeout(() => setShowErrorDialog(false), 2000);
    }
  };

  const toggleSelection = (permission: PermissionOption) => {
    setSelectedPermissions(prevPermissions =>
      prevPermissions.map(prevPermission =>
        prevPermission.appId === permission.appId && prevPermission.mask === permission.mask
          ? { ...prevPermission, selected: !prevPermission.selected }
          : prevPermission
      )
    );
  };

  const distribuitionData = distribuition?.list?.[0] ?? {
    emails: [],
    entid: 0,
    eventCode: 0,
    eventDescription: '',
    id: '',
    permissions: [],
    users: []
  };

  return (
    <Background>
      <Container fluid classNames={{ root: 'distribuition' }}>
        <Header pageTitle={mode === MODE.NEW ? `${lang.DISTRIBUITIONS_ADD_BUTTON}` : `${lang.GLOBAL_EVENT}: ${id}`} />
        <DistribuitionForm
          mode={mode}
          lang={lang}
          isNew={mode === MODE.NEW ? true : false}
          distribuition={distribuitionData}
          eventsList={events?.events ?? []}
          submitForm={handleSubmit}
          deleteDistribuition={handleDelete}
          permissionOptions={selectedPermissions}
          togglePermissions={toggleSelection}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
        <CustomModal
          opened={showSuccessDialog}
          onClose={() => setShowSuccessDialog(false)}
          message={successMessage}
          backgroundColor="#3BDF70"
          textColor="black"
        />

        <CustomModal
          opened={showErrorDialog}
          onClose={() => setShowErrorDialog(false)}
          message={errorMessage}
          backgroundColor="#E3353F"
          textColor="white"
        />
      </Container>
    </Background>
  );
};

export default Distribuition;
